/**
 * Shared controls.
 *
 * Every interactive element here is at least 44 pixels tall, which is the smallest target
 * that stays reliably tappable on a phone held one-handed.
 */

import {
  useEffect,
  useId,
  useRef,
  type ChangeEvent,
  type ReactNode,
} from 'react';

/** Visual weight of a button. */
export type ButtonTone = 'primary' | 'accent' | 'quiet' | 'danger';

const TONE_CLASSES: Record<ButtonTone, string> = {
  primary: 'bg-brand text-on-brand border-transparent hover:bg-brand-ink',
  accent: 'bg-accent text-white border-transparent hover:bg-accent-ink',
  quiet: 'bg-transparent text-ink border-line-strong hover:bg-sunken',
  danger: 'bg-transparent text-missing-text border-missing/60 hover:bg-missing-soft',
};

/*
 * A disabled button drops its tone entirely rather than fading it. A primary button at 40%
 * opacity still reads as the loudest thing on the screen — and as something that is merely
 * loading — while white on a washed-out blue is unreadable either way.
 */
const DISABLED_CLASSES = 'disabled:cursor-not-allowed disabled:border-line'
  + ' disabled:bg-sunken disabled:text-ink-3';

/** Props of the shared button. */
export interface ButtonProps {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  tone?: ButtonTone;
  disabled?: boolean;
  /** Stretches the button to the width of its container. */
  full?: boolean;
  className?: string;
}

/** A labelled button in one of the four tones. */
export function Button({
  children,
  onClick,
  type = 'button',
  tone = 'quiet',
  disabled = false,
  full = false,
  className = '',
}: ButtonProps): ReactNode {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-11 items-center justify-center gap-2 rounded-control border px-4
        text-base font-semibold transition-colors ${DISABLED_CLASSES}
        ${TONE_CLASSES[tone]} ${full ? 'w-full' : ''} ${className}`}
    >
      {children}
    </button>
  );
}

/** A square icon-only button; `label` becomes its accessible name. */
export function IconButton({
  label,
  onClick,
  children,
  disabled = false,
  className = '',
}: {
  label: string;
  onClick?: () => void;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
}): ReactNode {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-control
        text-ink-2 transition-colors hover:bg-sunken hover:text-ink disabled:opacity-30 ${className}`}
    >
      {children}
    </button>
  );
}

/** A labelled single-line text input. */
export function TextField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder = '',
  autoFocus = false,
  hint,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'date';
  placeholder?: string;
  autoFocus?: boolean;
  hint?: string;
}): ReactNode {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-tiny font-semibold text-ink-2" htmlFor={id}>{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        className="h-11 rounded-control border border-line-strong bg-surface px-3 text-base text-ink
          placeholder:text-ink-3"
      />
      {hint === undefined ? null : <p className="text-tiny text-ink-3">{hint}</p>}
    </div>
  );
}

/** A labelled multi-line text input. */
export function TextArea({
  label,
  value,
  onChange,
  rows = 4,
  placeholder = '',
  hint,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  hint?: string;
}): ReactNode {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-tiny font-semibold text-ink-2" htmlFor={id}>{label}</label>
      <textarea
        id={id}
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-control border border-line-strong bg-surface p-3 text-base text-ink
          placeholder:text-ink-3"
      />
      {hint === undefined ? null : <p className="text-tiny text-ink-3">{hint}</p>}
    </div>
  );
}

/** A labelled on/off switch rendered as a row with the label on the left. */
export function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}): ReactNode {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between gap-4 py-3 text-left"
    >
      <span className="flex flex-col">
        <span className="text-row font-medium text-ink">{label}</span>
        {description === undefined ? null : <span className="text-tiny text-ink-2">{description}</span>}
      </span>
      <span
        className={`relative h-7 w-12 shrink-0 rounded-full border transition-colors
          ${checked ? 'border-brand bg-brand' : 'border-line-strong bg-sunken'}`}
        aria-hidden="true"
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-[left]
            ${checked ? 'left-6' : 'left-0.5'}`}
        />
      </span>
    </button>
  );
}

/**
 * A modal dialog: a bottom sheet on a phone, a centred panel on a wide screen.
 *
 * Focus moves into the dialog on open and Escape closes it, so it is operable from a keyboard
 * as well as by touch.
 */
export function Dialog({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}): ReactNode {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    panel.current?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative w-full max-w-lg rounded-t-2xl border border-line bg-surface
          pb-[env(safe-area-inset-bottom)] sm:rounded-2xl"
      >
        <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <h2 id={titleId} className="text-title font-bold">{title}</h2>
          <IconButton label="Close" onClick={onClose}>
            <span aria-hidden="true" className="text-xl leading-none">×</span>
          </IconButton>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
        {footer === undefined ? null : (
          <div className="flex justify-end gap-2 border-t border-line px-5 py-4">{footer}</div>
        )}
      </div>
    </div>
  );
}

/**
 * Text shown in place of a list that has no entries yet, with an optional call to action.
 *
 * The drawing is an empty roster: name rules with one unfilled state box each. It carries the
 * same meaning as the words — there is nothing here yet — and gives the screen a centre of
 * gravity instead of a lone paragraph.
 */
export function EmptyState({ message, action }: { message: string; action?: ReactNode }): ReactNode {
  return (
    <div className="flex flex-col items-start gap-5 px-5 py-10 text-ink-2">
      <svg width="116" height="80" viewBox="0 0 116 80" aria-hidden="true" focusable="false">
        {[0, 1, 2].map((row) => {
          const filled = row === 2;
          return (
            <g key={row} transform={`translate(0 ${row * 28})`}>
              <rect x="0" y="6" width={64 - row * 12} height="7" rx="3.5" fill="var(--color-line-strong)" />
              <rect
                x="92"
                y="0"
                width="22"
                height="22"
                rx="7"
                fill={filled ? 'var(--color-brand)' : 'none'}
                stroke={filled ? 'var(--color-brand)' : 'var(--color-line-strong)'}
                strokeWidth="2"
              />
              {filled ? (
                <path
                  d="M97.5 11.5 101 15 108.5 7"
                  fill="none"
                  stroke="var(--color-on-brand)"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : null}
            </g>
          );
        })}
      </svg>
      <p className="max-w-prose text-base">{message}</p>
      {action}
    </div>
  );
}
